import { DateTime } from "luxon";
import { Avatar, Box, Paper, Typography, useTheme } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import AssignmentIcon from "@mui/icons-material/Assignment";
import PendingIcon from "@mui/icons-material/Pending";
import { MessagesByDay } from "./MyMessagesTypes";
import { TaskerMessage } from "@t3/shared";
import { FirebaseImage, StatusIcon } from "@/components/common";

type StatBoxProps = {
  icon: React.ReactNode;
  count: number;
  label: string;
  color: "success" | "error" | "warning" | "info";
};

const StatBox = ({ icon, count, label, color }: StatBoxProps) => {
  const theme = useTheme();

  return (
    <Box
      data-cypress={`stat-${label.toLowerCase()}`}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        bgcolor: `${theme.palette[color].main}08`,
        color: theme.palette[color].main,
        px: { xs: 1.5, sm: 1.75 },
        py: { xs: 1.5, sm: 1.75 },
        borderBottom: `3px solid ${theme.palette[color].main}`,
      }}
    >
      <Avatar
        sx={{
          bgcolor: theme.palette[color].main,
          color: "white",
          width: 24,
          height: 24,
        }}
      >
        {icon}
      </Avatar>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          minWidth: 0,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontSize: { xs: "1.375rem", sm: "1.5rem" },
            lineHeight: 1.1,
            fontWeight: 800,
            color: theme.palette[color].main,
          }}
        >
          {count}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            fontWeight: 600,
            fontSize: { xs: "0.65rem", sm: "0.7rem" },
            textTransform: "uppercase",
            letterSpacing: "0.3px",
            color: theme.palette.text.secondary,
          }}
        >
          {label}
        </Typography>
      </Box>
    </Box>
  );
};

type ThumbnailGridProps = {
  messages: TaskerMessage[];
};

const ThumbnailGrid = ({ messages }: ThumbnailGridProps) => {
  const theme = useTheme();

  // Get all media files from messages with their status
  const allMediaWithStatus = messages.flatMap((message) =>
    (message.mediaFiles || []).map((media) => ({
      url: media.url,
      contentType: media.contentType,
      status: message.reviewStatus,
      messageId: message.id,
    })),
  );

  if (allMediaWithStatus.length === 0) {
    return null;
  }

  const isImage = (contentType: string | undefined | null) => {
    if (!contentType) return false;
    return contentType.startsWith("image/");
  };

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: `repeat(auto-fill, minmax(max(${allMediaWithStatus.length > 12 ? "14" : "20"}%, 100px), 1fr))`,
        gap: 1,
        width: "100%",
      }}
    >
      {allMediaWithStatus.map((media, index) => (
        <Box
          key={`${media.messageId}-${media.url}-${index}`}
          data-cypress="message-thumbnail"
          sx={{
            position: "relative",
            width: "100%",
            aspectRatio: "1",
            borderRadius: 1,
            overflow: "hidden",
            border: `2px solid ${theme.palette.grey[200]}`,
            boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
          }}
        >
          {isImage(media.contentType) ? (
            <FirebaseImage
              firebaseUrl={media.url}
              alt={`Message media ${index + 1}`}
              width="100%"
              height="100%"
              objectFit="cover"
              fallbackText="Image"
            />
          ) : (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: theme.palette.grey[100],
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  fontWeight: 600,
                  textTransform: "uppercase",
                  fontSize: "0.7rem",
                }}
              >
                {media.contentType ? media.contentType.split("/")[0] : "File"}
              </Typography>
            </Box>
          )}

          {/* Status icon overlay */}
          <Box
            sx={{
              position: "absolute",
              bottom: 3,
              right: 3,
              bgcolor: "white",
              borderRadius: "50%",
              p: 0.2,
              display: "flex",
              boxShadow: "0 1px 3px rgba(0,0,0,0.25)",
            }}
          >
            <StatusIcon status={media.status} />
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export const DayGroup = ({
  date,
  data,
  onOpen,
}: {
  date: string;
  data: MessagesByDay[string];
  onOpen: () => void;
}) => {
  const theme = useTheme();

  // Use Luxon to parse the date string
  const dateObj = DateTime.fromISO(date);
  const today = DateTime.now().startOf("day");
  const yesterday = today.minus({ days: 1 });

  let dayLabel = date;
  if (dateObj.hasSame(today, "day")) {
    dayLabel = "Today";
  } else if (dateObj.hasSame(yesterday, "day")) {
    dayLabel = "Yesterday";
  } else {
    dayLabel = dateObj.toLocaleString({
      weekday: "long",
      month: "long",
      day: "numeric",
    });
  }

  const stats = [
    {
      icon: <CheckCircleIcon sx={{ fontSize: "1rem" }} />,
      count: data.approved,
      label: "Approved",
      color: "success" as const,
    },
    {
      icon: <CancelIcon sx={{ fontSize: "1rem" }} />,
      count: data.rejected,
      label: "Rejected",
      color: "error" as const,
    },
    {
      icon: <PendingIcon sx={{ fontSize: "1rem" }} />,
      count: data.pending,
      label: "Pending",
      color: "warning" as const,
    },
    {
      icon: <AssignmentIcon sx={{ fontSize: "1rem" }} />,
      count: data.messages.length,
      label: "Total",
      color: "info" as const,
    },
  ];

  // Calculate progress bar segments
  const total = data.approved + data.rejected + data.pending;
  const approvedPercent =
    total > 0 ? Math.round((data.approved / total) * 100) : 0;
  const rejectedPercent =
    total > 0 ? Math.round((data.rejected / total) * 100) : 0;
  const pendingPercent =
    total > 0 ? Math.round((data.pending / total) * 100) : 0;

  return (
    <Paper
      data-cypress="day-group"
      elevation={0}
      sx={{
        cursor: "pointer",
        transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
        borderRadius: 1,
        border: `2px solid #aaa`,
        background: `radial-gradient(circle at top left, ${theme.palette.grey[300]}, white)`,
        "&:hover": {
          boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
          transform: "scale(1.01)",
          transformOrigin: "center bottom",
          borderColor: "#888",
        },
      }}
      onClick={onOpen}
    >
      {/* Date Header */}
      <Box
        sx={{
          position: "sticky",
          top: "env(safe-area-inset-top, 0)",
          zIndex: 10,
          background: `radial-gradient(circle at top left, ${theme.palette.grey[300]}, white)`,
        }}
      >
        <Typography
          variant="h6"
          data-cypress="day-date"
          sx={{
            fontWeight: 700,
            px: { xs: 2, sm: 2.5 },
            py: { xs: 1.25, sm: 1.5 },
            color: theme.palette.text.primary,
            fontSize: { xs: "0.9rem", sm: "1rem" },
          }}
        >
          {dayLabel}
        </Typography>

        {/* Progress bar showing distribution of states */}
        <Box
          sx={{
            display: "flex",
            height: "4px",
            width: "100%",
          }}
        >
          {approvedPercent > 0 && (
            <Box
              sx={{
                width: `${approvedPercent}%`,
                bgcolor: theme.palette.success.main,
                transition: "width 0.5s ease",
              }}
            />
          )}
          {rejectedPercent > 0 && (
            <Box
              sx={{
                width: `${rejectedPercent}%`,
                bgcolor: theme.palette.error.main,
                transition: "width 0.5s ease",
              }}
            />
          )}
          {pendingPercent > 0 && (
            <Box
              sx={{
                width: `${pendingPercent}%`,
                bgcolor: theme.palette.warning.main,
                transition: "width 0.5s ease",
              }}
            />
          )}
          {/* Fallback grey bar if no messages */}
          {total === 0 && (
            <Box
              sx={{
                width: "100%",
                bgcolor: theme.palette.grey[300],
              }}
            />
          )}
        </Box>
      </Box>
      {/* Content */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
        }}
      >
        {/* Stats */}
        <Box
          sx={{
            display: "grid",
            width: { xs: "100%", sm: "fit-content" },
            gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(1, 1fr)" },
            gridTemplateRows: "repeat(auto-fill, minmax(min-content, 0))",
          }}
        >
          {stats.map((stat) => (
            <StatBox key={stat.label} {...stat} />
          ))}
        </Box>

        {/* Thumbnails */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            p: 1,
            overflow: "hidden",
          }}
        >
          <ThumbnailGrid messages={data.messages} />
        </Box>
      </Box>
    </Paper>
  );
};
